import React, { memo, useCallback, useMemo, useState } from 'react'
import useSWR, { mutate as globalMutate } from 'swr'
import { PAYMENT_GATEWAY, PAYMENT_METHOD } from 'src/assets/SettingsConfig'
import { IPaymentGateways } from 'src/lib/types/settings'
import Button from '@mui/material/Button'
import ReplayIcon from 'mdi-material-ui/Replay'

import { apiURI, permissionType } from 'src/lib/utils/constants'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { getRequest } from 'src/lib/core/api-client'
import { useUser } from 'src/lib/contexts/UserContext'
import LoadingList from 'src/components/common/ui-preview/skeletons/LoadingList'
import {
  Gateway,
  PaymentMethodResponse,
  PaymentGatewayPriority,
  ValidMethods,
  SelectionArgs
} from 'src/lib/types/paymentSettings'
import {
  createPayload,
  getGatewayMode,
  getNormalizedAllowedGateways,
  getNormalizedPaymentMethods,
  getOptimisticData,
  getPaymentGateWayMode,
  getPriorityValue,
  updateGateway,
  PaymentModeScope
} from 'src/lib/utils/paymentSettings'
import PaymentMethodRow from './PaymentMethodRow'
import { useMerchant } from 'src/lib/contexts/MerchantContext'

const normalizedPaymentMethods: Record<string, IPaymentGateways> = getNormalizedPaymentMethods()
const normalizedAllowedGateways: Record<string, IPaymentGateways> = getNormalizedAllowedGateways()

const getValidMethods = (methods: PaymentMethodResponse[]) => {
  return methods.filter(method => Boolean(normalizedPaymentMethods[method.mode]))
}

const METHODS_TO_FILTER = [PAYMENT_METHOD.COD, PAYMENT_METHOD.SPLIT_COD]
const METHODS_TO_SHOW_ONLY_TEXT = [
  PAYMENT_METHOD.SNPM,
  PAYMENT_METHOD.VELOCITY,
  PAYMENT_METHOD.BHARATX,
  PAYMENT_METHOD.SIMPL,
  PAYMENT_METHOD.SIMPL_PL
]
const METHODS_TO_SHOW_AT_END = [
  PAYMENT_METHOD.BHARATX,
  PAYMENT_METHOD.SNPM,
  PAYMENT_METHOD.VELOCITY,
  PAYMENT_METHOD.SIMPL,
  PAYMENT_METHOD.SIMPL_PL
]

const getValidMethodsWithAllowedGateways = (methods: PaymentMethodResponse[], modeType?: PaymentModeScope) => {
  const validMethods = getValidMethods(methods)
  return validMethods
    .map(method => {
      return {
        method: method.mode,
        availableGateways: (method.available_gateways ?? [])
          .filter((gateway, idx, gateways) => gateways.indexOf(gateway) === idx)
          .filter(gateway => Boolean(normalizedAllowedGateways[gateway])),
        primaryGateway: method.gateways?.find(
          gateway => Boolean(normalizedAllowedGateways[gateway.pg_name]) && gateway.priority === 0
        ),
        secondaryGateway: method.gateways?.find(
          gateway => Boolean(normalizedAllowedGateways[gateway.pg_name]) && gateway.priority === 1
        ),
        shouldOnlyShowText: METHODS_TO_SHOW_ONLY_TEXT.includes(method.mode),
        isComingSoon: modeType === 'SUBSCRIPTION' && method.available_gateways === null
      }
    })
    .filter(mode => {
      // don't show cod and split cod in the list
      return !METHODS_TO_FILTER.includes(mode.method)
    })
    .sort((methodA, methodB) => {
      if (modeType === 'SUBSCRIPTION') {
        return 0
      }
      if (METHODS_TO_SHOW_AT_END.includes(methodA.method)) {
        return 1
      }
      if (METHODS_TO_SHOW_AT_END.includes(methodB.method)) {
        return -1
      }
      if (methodA.method.toString() < methodB.method.toString()) {
        return -1
      } else if (methodA.method.toString() > methodB.method.toString()) {
        return 1
      }
      return 0
    })
}

const PaymentMethods: React.FC<{ modeType?: PaymentModeScope }> = ({ modeType }) => {
  const {
    getters: { merchant }
  } = useMerchant()

  const MID = merchant?.accountId ?? ''

  const {
    data: methodsResponse,
    error,
    isLoading,
    mutate
  } = useSWR(
    [`/md/merchant/${MID}/payment-modes${modeType ? `?purchase_type=${modeType}` : ''}`, apiURI.FLO_KRATOS_CHECKOUT],
    ([url, baseURI]) => getRequest(url, baseURI),
    {
      dedupingInterval: 0
    }
  )

  const [isUpdatingPG, setIsUpdatingPG] = useState(false)

  const methods = useMemo(() => (methodsResponse?.data as PaymentMethodResponse[]) || [], [methodsResponse])
  const validMethods: ValidMethods = getValidMethodsWithAllowedGateways(methods, modeType)

  const { canAccess } = useUser()
  const hasAutoPGAccess = canAccess(permissionType.AUTO_PG_ROUTING, 0)

  const handleGatewayUpdate = useCallback(
    async (args: SelectionArgs): Promise<void> => {
      if (!methodsResponse) {
        return
      }

      const { priority, method, bulkUpdate = false, swapGateways = false } = args
      const isRemoval = 'isRemoval' in args ? args.isRemoval : false
      const gateway = 'gateway' in args ? args.gateway : undefined

      const priorityValue = getPriorityValue(priority)
      if (priorityValue === -1) {
        errorToast('Error while setting current gateway')
        console.error('invalid payment gateway priority')
        return
      }

      const optimisticData = getOptimisticData(methods, {
        method,
        priority: priorityValue,
        swapGateways,
        ...(isRemoval ? { isRemoval: true } : { isRemoval: false, gateway: gateway as PAYMENT_GATEWAY })
      })

      const payload = createPayload({
        bulkUpdate,
        gateway: gateway || '',
        optimisticData,
        method,
        isRemoval
      })

      try {
        setIsUpdatingPG(true)
        await mutate(updateGateway(payload, MID, modeType), {
          optimisticData: { ...methodsResponse, data: optimisticData, error: {}, headers: {} },
          rollbackOnError: error => {
            return true
          },
          revalidate: false
        })
        globalMutate([
          `/merchant-attributes/settings?payments=true`,apiURI.FLO_KRATOS_CHECKOUT
        ])
        successToast(`Successfully updated payment gateway`)
      } catch (error: any) {
        errorToast(error || `Something went wrong updating payment gateway`)
      } finally {
        setIsUpdatingPG(false)
      }
    },
    [methodsResponse, methods, MID, mutate, modeType]
  )

  if (isLoading) {
    return (
      <LoadingList
        length={3}
        skeleton={{
          layout: 'row',
          justify: 'space-between',
          config: [
            { height: 1, width: 6 },
            { height: 1, width: 6 },
            { height: 1, width: 1 },
            { height: 1, width: 6 }
          ]
        }}
      />
    )
  }

  if (error) {
    return (
      <div className='flex justify-center items-center'>
        <Button aria-label='reload' variant='outlined'>
          <p>Something went wrong fetching gateways. Retry?</p>
          <ReplayIcon />
        </Button>
      </div>
    )
  }

  if (!methodsResponse || methods.length === 0) {
    return <></>
  }

  return (
    <div className='w-full rounded-lg border border-[#F0F0F0]'>
      <ul className={`grid grid-rows-${validMethods.length + 1} list-none divide-y-1`}>
        <li className='grid grid-cols-3 divide-x-2 bg-[#fafafa] font-medium'>
          <span className='p-4 items-center justify-center'>Payment Method</span>
          <span className='p-4 flex items-center justify-center'>Primary PG</span>
          <span className='p-4 flex items-center justify-center'>Alternate PG</span>
        </li>
        {validMethods.map(({ method, ...rest }) => (
          <PaymentMethodRow
            key={method}
            method={method}
            isDisabled={isUpdatingPG}
            onGatewaySelect={handleGatewayUpdate}
            {...rest}
          />
        ))}
      </ul>
    </div>
  )
}

export default PaymentMethods
